import React, {
  useEffect, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import { showSuccessToast } from '../../../shared/utils/toastManager';
import ManageCalendarsTable from './presentational/tables/ManageCalendarsTable';
import AddCalendarModal from './presentational/modals/AddCalendarModal';
import ConfirmModal from './presentational/modals/ConfirmModal';
import {
  fetchCalendars, archiveCalendars, unarchiveCalendars, deleteCalendars,
} from '../../calendars/ducks/calendarsActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const getBackLabel = (backPath, isMobile) => {
  if (isMobile && backPath.startsWith('/dashboard/calendars')) return 'Back to Calendars';
  if (backPath.startsWith('/dashboard/manage')) return 'Back to Manage';
  return 'Back to Home';
};

const ManageCalendars = ({ backPath, isMobile }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const tableRef = useRef(null);

  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const isCalendarSubmitting = useSelector((state) => state.calendars.ui.isCalendarSubmitting);
  const calendarsRaw = useSelector((state) => state.calendars.data.unparsedCalendars);

  const [isArchived, setIsArchived] = useState(false);
  const [calendarToEdit, setCalendarToEdit] = useState(null);
  const [calendarToDelete, setCalendarToDelete] = useState(null);
  const [calendarToArchive, setCalendarToArchive] = useState(null);
  const [calendarToRestore, setCalendarToRestore] = useState(null);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);

  useEffect(() => {
    if (accountCode) {
      dispatch(fetchCalendars(accountCode));
    }
  }, [accountCode, dispatch]);

  // Deep link from a calendar's own panel manage button
  useEffect(() => {
    const scrollToCalendarId = location.state?.scrollToCalendarId;
    if (scrollToCalendarId == null || calendarsRaw.length === 0) return;
    tableRef.current?.scrollToCalendar(scrollToCalendarId);
    history.replace(location.pathname, { ...location.state, scrollToCalendarId: undefined });
  }, [location.state?.scrollToCalendarId, calendarsRaw]);

  const handleBulkAction = (action, ids) => {
    setBulkAction(action);
    setBulkSelectedIds(ids);
  };

  const clearBulkAction = () => {
    setBulkAction(null);
    setBulkSelectedIds([]);
  };

  const confirmRestoreCalendar = () => {
    dispatch(unarchiveCalendars(accountCode, [calendarToRestore.id], {
      onSuccess: () => {
        showSuccessToast(`${calendarToRestore.name} has been restored.`);
        setCalendarToRestore(null);
      },
    }));
  };

  const confirmSingleArchive = () => {
    dispatch(archiveCalendars(accountCode, [calendarToArchive.id], {
      onSuccess: () => {
        showSuccessToast(`${calendarToArchive.name} has been archived.`);
        setCalendarToArchive(null);
      },
    }));
  };

  const confirmSingleDelete = () => {
    dispatch(deleteCalendars(accountCode, [calendarToDelete.id], {
      onSuccess: () => {
        showSuccessToast(`${calendarToDelete.name} has been deleted.`);
        setCalendarToDelete(null);
      },
    }));
  };

  const confirmBulkArchive = () => {
    dispatch(archiveCalendars(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} calendar${bulkSelectedIds.length === 1 ? '' : 's'} archived.`);
        clearBulkAction();
      },
    }));
  };

  const confirmBulkDelete = () => {
    dispatch(deleteCalendars(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} calendar${bulkSelectedIds.length === 1 ? '' : 's'} deleted.`);
        clearBulkAction();
      },
    }));
  };

  const confirmBulkRestore = () => {
    dispatch(unarchiveCalendars(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} calendar${bulkSelectedIds.length === 1 ? '' : 's'} restored.`);
        clearBulkAction();
      },
    }));
  };

  const confirmBulkDeleteArchived = () => {
    dispatch(deleteCalendars(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} calendar${bulkSelectedIds.length === 1 ? '' : 's'} deleted.`);
        clearBulkAction();
      },
    }));
  };

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <BackLink type='button' onClick={() => history.push(backPath)}>
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            {getBackLabel(backPath, isMobile)}
          </BackLink>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Manage Calendars
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          <Button
            name='Add Calendar'
            icon='add-calendar'
            iconColor='#FFF'
            txtColor='#FFF'
            bgColor='#1A1A1A'
            borderColor='#1A1A1A'
            customBorderRadius='4px'
            styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
            styledWrapperProps={{ boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)' }}
            onClick={() => history.push('/calendar/create', { from: location.pathname })}
          />
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <ManageCalendarsTable
          ref={tableRef}
          isArchived={isArchived}
          onToggleArchived={setIsArchived}
          onEditCalendar={setCalendarToEdit}
          onArchiveCalendar={setCalendarToArchive}
          onDeleteCalendar={setCalendarToDelete}
          onRestoreCalendar={setCalendarToRestore}
          onBulkAction={handleBulkAction}
        />
      </BottomSection>

      <AddCalendarModal
        isOpen={!!calendarToEdit}
        onClose={() => setCalendarToEdit(null)}
        calendar={calendarToEdit}
      />

      <ConfirmModal
        isOpen={!!calendarToArchive}
        onClose={() => setCalendarToArchive(null)}
        onConfirm={confirmSingleArchive}
        title='Archive Calendar'
        message={`Are you sure you want to archive ${calendarToArchive?.name}? You can restore it from the Archived tab at any time.`}
        confirmLabel='Archive'
        destructive={false}
        confirmIcon={null}
        isLoading={isCalendarSubmitting}
      />

      <ConfirmModal
        isOpen={!!calendarToRestore}
        onClose={() => setCalendarToRestore(null)}
        onConfirm={confirmRestoreCalendar}
        title='Restore Calendar'
        message={`Are you sure you want to restore ${calendarToRestore?.name}? It will be moved back to the active calendars list.`}
        confirmLabel='Restore'
        confirmIcon='restore'
        destructive={false}
        isLoading={isCalendarSubmitting}
      />

      <ConfirmModal
        isOpen={!!calendarToDelete}
        onClose={() => setCalendarToDelete(null)}
        onConfirm={confirmSingleDelete}
        title='Delete Calendar'
        message={`Are you sure you want to delete ${calendarToDelete?.name}? This action cannot be undone.`}
        isLoading={isCalendarSubmitting}
      />

      <ConfirmModal
        isOpen={bulkAction === 'archiveCalendars'}
        onClose={clearBulkAction}
        onConfirm={confirmBulkArchive}
        title={`Archive ${bulkSelectedIds.length} Calendar${bulkSelectedIds.length === 1 ? '' : 's'}`}
        message='Are you sure you want to archive these calendars? You can restore them from the Archived tab at any time.'
        confirmLabel='Archive'
        destructive={false}
        confirmIcon={null}
        isLoading={isCalendarSubmitting}
      />

      <ConfirmModal
        isOpen={bulkAction === 'deleteCalendars'}
        onClose={clearBulkAction}
        onConfirm={confirmBulkDelete}
        title={`Delete ${bulkSelectedIds.length} Calendar${bulkSelectedIds.length === 1 ? '' : 's'}`}
        message='Are you sure you want to delete these calendars? This action cannot be undone.'
        isLoading={isCalendarSubmitting}
      />

      <ConfirmModal
        isOpen={bulkAction === 'restoreCalendars'}
        onClose={clearBulkAction}
        onConfirm={confirmBulkRestore}
        title={`Restore ${bulkSelectedIds.length} Calendar${bulkSelectedIds.length === 1 ? '' : 's'}`}
        message='Are you sure you want to restore these calendars? They will be moved back to the active calendars list.'
        confirmLabel='Restore'
        confirmIcon='restore'
        destructive={false}
        isLoading={isCalendarSubmitting}
      />

      <ConfirmModal
        isOpen={bulkAction === 'deleteArchivedCalendars'}
        onClose={clearBulkAction}
        onConfirm={confirmBulkDeleteArchived}
        title={`Delete ${bulkSelectedIds.length} Calendar${bulkSelectedIds.length === 1 ? '' : 's'}`}
        message='Are you sure you want to delete these calendars? This action cannot be undone.'
        isLoading={isCalendarSubmitting}
      />
    </Container>
  );
};

ManageCalendars.propTypes = {
  backPath: PropTypes.string,
  isMobile: PropTypes.bool,
};

ManageCalendars.defaultProps = {
  backPath: '/dashboard/manage',
  isMobile: false,
};

export default ManageCalendars;
